import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import Logo from './Logo';
import ThemeToggle from './ThemeToggle';
import './header.css';

// Literal markup/classes from the confirmed reference
// (.agent/templates/sonic-motion-reference.html: .a-nav/.a-brand/.a-logo/
// .a-live/.a-links/.a-cta/.a-menu-btn). Link set matches the reference's
// #primaryNav exactly (Episodes/It Guhso/Tech/Merch/Directory), which is
// also the site's real nav taxonomy (It Guhso = /blog, per legacy
// Navbar.js).
const NAV_LINKS = [
  { to: '/episodes', label: 'Episodes' },
  { to: '/blog', label: 'It Guhso' },
  { to: '/tech', label: 'Tech' },
  { to: '/merch', label: 'Merch' },
  { to: '/directory', label: 'Directory' }
];

// Read-only broadcast state. Until a backend status feed is available, the
// parent controls this value; visitors cannot manufacture a live state by
// clicking the indicator.
const LiveBadge = ({ isLive = false }) => (
  <span
    className={`a-live ${isLive ? 'is-on' : 'is-off'}`}
    role="status"
    aria-label={isLive ? 'Live now' : 'Not currently live'}
  >
    <span className="dot" aria-hidden="true" />
    Live
  </span>
);

const ShoppingBag = ({ itemCount }) => (
  <Link
    to="/cart"
    className="a-nav-cart"
    aria-label={`Shopping bag, ${itemCount} ${itemCount === 1 ? 'item' : 'items'}`}
  >
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6 8V6a6 6 0 0 1 12 0v2" />
      <path d="M4 8h16l-1 13H5L4 8Z" />
      <path d="M9 11v1M15 11v1" />
    </svg>
    {itemCount > 0 ? (
      <span className="a-nav-cart__badge" aria-hidden="true">
        {itemCount > 99 ? '99+' : itemCount}
      </span>
    ) : null}
  </Link>
);

// NOTE ON DEVIATION FROM THE LITERAL REFERENCE: the reference's nav markup
// has no Log In/account element at all — it's a static design mockup and
// doesn't depict signed-in state. Auth is real, working functionality
// (backed by GET /user/account), not decorative, so it isn't dropped to
// match the mockup pixel-for-pixel; it's added as one extra item inside
// .a-links, styled to sit naturally alongside the reference's nav treatment
// rather than as a separate bolt-on block. Flagged to Sean rather than
// silently resolved either way.
const HeaderAccountControl = ({ onNavigate }) => {
  const { isAuthenticated, canAccessExclusiveContent, user, loading } = useAuth();

  if (loading) {
    return <span className="a-account a-account--loading" aria-hidden="true" />;
  }

  if (!isAuthenticated) {
    return (
      <Link to="/signin" className="a-account a-account--login" onClick={onNavigate}>
        Log In
      </Link>
    );
  }

  const displayName = user?.name || user?.email || 'Account';
  const initial = displayName.charAt(0).toUpperCase();

  return (
    <Link
      to="/profile"
      className="a-account"
      aria-label={`Account: ${displayName}${canAccessExclusiveContent ? ' (member)' : ''}`}
      onClick={onNavigate}
    >
      <span className="a-account-avatar" aria-hidden="true">{initial}</span>
      <span className="a-account-name">{displayName}</span>
      {canAccessExclusiveContent ? <span className="a-account-badge">Member</span> : null}
    </Link>
  );
};

// Mobile-only secondary CTA — hidden on desktop via CSS, shown inside the
// .a-links dropdown panel below 820px alongside the account control.
const MobileAuthCta = ({ onNavigate }) => {
  const { isAuthenticated, canAccessExclusiveContent } = useAuth();

  if (!isAuthenticated) {
    return (
      <Link to="/register" className="a-mobile-cta" onClick={onNavigate}>
        Join the community
      </Link>
    );
  }

  if (!canAccessExclusiveContent) {
    return (
      <Link to="/membership/checkout" className="a-mobile-cta" onClick={onNavigate}>
        Become a member
      </Link>
    );
  }

  return null;
};

const Header = ({ isLive = false }) => {
  const location = useLocation();
  const { getItemCount } = useCart();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const itemCount = getItemCount();
  const navRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMenuOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const nav = navRef.current;
    nav.querySelector('#primaryNav a')?.focus();
    const handleKey = (event) => {
      if (event.key === 'Escape') {
        setIsMenuOpen(false);
        menuRef.current?.focus();
      }
      if (event.key !== 'Tab') return;
      const links = [...nav.querySelectorAll('a, button')].filter(el => el.getClientRects().length);
      const first = links[0], last = links[links.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    const desktop = window.matchMedia('(min-width: 1041px)');
    const resize = () => { if (desktop.matches) setIsMenuOpen(false); };
    desktop.addEventListener('change', resize);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      desktop.removeEventListener('change', resize);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isMenuOpen]);

  const handleMenuToggle = () => setIsMenuOpen((prev) => !prev);
  const closeMenu = () => setIsMenuOpen(false);

  return (
    <nav ref={navRef} className={`a-nav ${isMenuOpen ? 'has-open-menu' : ''}`} aria-label="Main navigation">
      <div className="a-brand">
        <Logo className={isLive ? 'a-logo--live' : ''} />
        <LiveBadge isLive={isLive} />
      </div>
      <div className={`a-links ${isMenuOpen ? 'is-open' : ''}`} id="primaryNav" aria-label="Main navigation">
        {NAV_LINKS.map((link) => (
          <NavLink
            key={link.to}
            to={link.to}
            className={({ isActive }) => {
              const isEpisodeDetail = link.to === '/episodes' && location.pathname.startsWith('/episode/');
              return isActive || isEpisodeDetail ? 'is-active' : undefined;
            }}
            onClick={closeMenu}
          >
            {link.label}
          </NavLink>
        ))}
        <Link to="/donation" className="a-nav-donate" onClick={closeMenu}>Donate</Link>
        <div className="a-links__account">
          <HeaderAccountControl onNavigate={closeMenu} />
          <MobileAuthCta onNavigate={closeMenu} />
        </div>
      </div>
      <div className="a-nav-actions">
        <ThemeToggle />
        <ShoppingBag itemCount={itemCount} />
        <div className="a-desktop-account">
          <HeaderAccountControl />
        </div>
        <button
          ref={menuRef}
          aria-expanded={isMenuOpen}
          aria-controls="primaryNav"
          aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          className="a-menu-btn"
          type="button"
          onClick={handleMenuToggle}
        >
          {isMenuOpen ? 'Close' : 'Menu'}
        </button>
      </div>
    </nav>
  );
};

export default Header;
